export const ARTICLES = [
  {
    slug: "energielabel-verplicht-bij-verkoop",
    title: "Energielabel verplicht bij verkoop of verhuur: dit moet u weten",
    description:
      "Is een energielabel verplicht bij de verkoop of verhuur van uw woning? Lees wanneer de verplichting geldt, wat de risico's zijn zonder label en hoe u het snel regelt.",
    date: "27 september 2026",
    readTime: "4 min leestijd",
    category: "Regelgeving",
    intro:
      "Gaat u uw woning verkopen of verhuren? Dan is een geldig energielabel verplicht. Toch is het voor veel woningeigenaren onduidelijk wanneer de verplichting precies geldt en wat er gebeurt als het label ontbreekt. In dit artikel zetten wij het helder op een rij.",
    sections: [
      {
        heading: "Wanneer is een energielabel verplicht?",
        paragraphs: [
          "De verplichting geldt in drie situaties: bij de verkoop van een woning, bij de verhuur van een woning en bij de oplevering van nieuwbouw. Het label moet al beschikbaar zijn op het moment dat u de woning aanbiedt — dus al bij het plaatsen van de advertentie, niet pas bij de overdracht.",
          "Het energielabel laat zien hoe energiezuinig de woning is, op een schaal van A++++ (zeer zuinig) tot G (weinig zuinig). Kopers en huurders krijgen zo eerlijk inzicht in de energieprestatie en de te verwachten energiekosten.",
        ],
      },
      {
        heading: "Wat is het risico zonder geldig label?",
        paragraphs: [
          "Heeft u bij verkoop of verhuur geen geldig energielabel, dan riskeert u een boete van de Inspectie Leefomgeving en Transport (ILT). Die boete kan oplopen tot honderden euro's. Daarnaast kan het ontbreken van het label de verkoop vertragen: steeds meer makelaars en verkoopplatforms controleren er actief op.",
          "Controleer daarom altijd eerst of uw woning al een geldig label heeft. Dat doet u eenvoudig in EP-online, de landelijke database van de Rijksdienst voor Ondernemend Nederland (RVO).",
        ],
      },
      {
        heading: "Hoe lang blijft een energielabel geldig?",
        paragraphs: [
          "Een geregistreerd energielabel is tien jaar geldig. Heeft u in die periode uw woning verduurzaamd — bijvoorbeeld met isolatie, HR++ glas of zonnepanelen — dan blijft het oude label ongewijzigd staan. Een heropname is dan zinvol: de verbeteringen leveren vrijwel altijd een beter label op, en dat ziet u terug in de verkoopwaarde en interesse van kopers.",
        ],
      },
      {
        heading: "Hoe vraagt u een energielabel aan?",
        paragraphs: [
          "De aanvraag is eenvoudig: u vult online uw woninggegevens in, wij plannen samen een moment voor de woningopname en een adviseur komt bij u langs. De opname gebeurt volgens de geldende EP-methodiek en duurt meestal minder dan een uur. Daarna wordt het label verwerkt en geregistreerd in EP-online.",
          "Directelabels voert professionele EP-W woningopnames uit in Rotterdam en omgeving, met vaste introductietarieven vanaf €139 inclusief btw. Vraag direct online aan — binnen twee minuten geregeld.",
        ],
      },
    ],
  },
  {
    slug: "energielabel-kosten-woning",
    title: "Wat kost een energielabel voor uw woning?",
    description:
      "Wat kost een energielabel? Lees waaruit de prijs bestaat, welke factoren de kosten beïnvloeden en waar u op moet letten bij het vergelijken van aanbieders.",
    date: "27 september 2026",
    readTime: "4 min leestijd",
    category: "Kosten",
    intro:
      "De kosten van een energielabel verschillen per aanbieder en per woning. In dit artikel leggen wij uit waaruit de prijs bestaat, welke factoren invloed hebben op het bedrag en waar u op moet letten bij het vergelijken van aanbieders — zodat u nooit voor verrassingen komt te staan.",
    sections: [
      {
        heading: "Waaruit bestaat de prijs van een energielabel?",
        paragraphs: [
          "Een energielabel bestaat uit twee onderdelen: de woningopname en de registratie. Bij de opname komt een adviseur bij u langs en brengt de woning in kaart volgens de geldende EP-methodiek: isolatie, kozijnen en beglazing, verwarming, warm water en ventilatie. Daarna wordt het label berekend, verwerkt en geregistreerd in EP-online, de landelijke database van het RVO.",
          "Let er bij het vergelijken van prijzen op dat beide onderdelen zijn inbegrepen. Sommige aanbieders rekenen de registratie of voorrijkosten apart.",
        ],
      },
      {
        heading: "Welke factoren beïnvloeden de kosten?",
        paragraphs: [
          "De belangrijkste factor is het woningtype: een appartement is eenvoudiger op te nemen dan een grote vrijstaande woning. Ook de woonoppervlakte, de complexiteit van de woning en de locatie spelen een rol. Heeft u het label op zeer korte termijn nodig, dan kan een spoedaanvraag invloed hebben op de planning en prijs.",
        ],
      },
      {
        heading: "De introductietarieven van Directelabels",
        paragraphs: [
          "Wij werken met vaste vanaf-prijzen, inclusief btw, woningopname en registratie in EP-online: appartement vanaf €139, tussenwoning vanaf €149, hoekwoning vanaf €159, 2-onder-1-kapwoning vanaf €169 en vrijstaande woning vanaf €199. U ontvangt altijd vooraf een definitieve offerte.",
          "Bekijk alle tarieven op onze tarievenpagina of vraag direct een vrijblijvende aanvraag aan.",
        ],
      },
      {
        heading: "Waar moet u op letten bij een aanbieder?",
        paragraphs: [
          "Kies een aanbieder die duidelijk communiceert over wat er is inbegrepen, de woning volgens de geldende EP-methodiek opneemt en het label registreert in EP-online. Een energielabel is een officieel document dat bij uw woning hoort — zorgvuldigheid is belangrijker dan de laagste prijs.",
        ],
      },
    ],
  },
  {
    slug: "energielabel-verbeteren-tips",
    title: "Energielabel verbeteren: zo krijgt uw woning een beter label",
    description:
      "Wilt u een beter energielabel voor uw woning? Ontdek welke maatregelen het meeste effect hebben en hoe een heropname na verduurzaming werkt.",
    date: "27 september 2026",
    readTime: "4 min leestijd",
    category: "Verduurzaming",
    intro:
      "Een beter energielabel is meer dan een mooi cijfer. Het vergroot de aantrekkelijkheid van uw woning bij verkoop of verhuur en geeft kopers vertrouwen in lagere energiekosten. In dit artikel leest u welke verbeteringen het meeste effect hebben en hoe u een nieuw label vastlegt.",
    sections: [
      {
        heading: "Waarom een beter label loont",
        paragraphs: [
          "Woningen met een zuinig label zijn aantoonbaar populairder op de woningmarkt: ze verkopen sneller en vaak voor een hogere prijs. Ook geldverstrekkers kijken mee — sommige hypotheekverstrekkers geven rentekorting bij een energiezuinige woning. Voor verhuurders geldt dat een goed label de woning aantrekkelijker maakt voor huurders.",
        ],
      },
      {
        heading: "Welke maatregelen hebben het meeste effect?",
        paragraphs: [
          "Isolatie heeft doorgaans de grootste invloed op het label: denk aan dak-, gevel- en vloerisolatie. Daarna volgen betere beglazing (HR++ of triple glas), een zuinige verwarmingsinstallatie zoals een warmtepomp, en zonnepanelen. Ook kleinere maatregelen zoals radiatorfolie en een goede ventilatie met warmteterugwinning tellen mee.",
          "Welke combinatie voor uw woning het beste rendement geeft, hangt af van het bouwjaar en de huidige staat van de woning.",
        ],
      },
      {
        heading: "Heropname na verduurzaming",
        paragraphs: [
          "Heeft u uw woning verduurzaamd, maar staat er nog een ouder label geregistreerd? Dan is een heropname zinvol. Tijdens een nieuwe woningopname worden alle verbeteringen meegewogen, waardoor het label vrijwel altijd omhoog gaat. Het nieuwe label is daarna weer tien jaar geldig.",
          "Bewaar facturen en specificaties van de uitgevoerde maatregelen: die helpen de adviseur om de verbeteringen correct mee te nemen in de beoordeling.",
        ],
      },
      {
        heading: "Hoe vraagt u een heropname aan?",
        paragraphs: [
          "Een heropname werkt hetzelfde als een eerste aanvraag. U vult de woninggegevens online in, wij plannen de opname en het nieuwe label wordt geregistreerd in EP-online. Geef in het opmerkingenveld aan welke verduurzamingen zijn uitgevoerd — dan bereiden wij de opname daarop voor.",
        ],
      },
    ],
  },
];
